import { useMemo } from 'react';
import { UserLoginStatus } from '@sib/api';
import { useTranslation } from '@sib/i18n';
import { cn, InformationWrapper, Tooltip } from '@sib/ui';
import { CheckCircle, EmptyInvitations } from '@sib/ui/icons';
import { Invitee } from '@spendbrain/types/user';
import { InviteesListItem } from './InviteesListItem';
import { InviteesListSkeleton } from './InviteesListSkeleton';

interface InviteesListProps {
  data: Invitee[];
  onRemove?: (invitee: Invitee) => void;
  onResend?: (invitee: Invitee) => void;
  loading?: boolean;
}

export const InviteesList = ({
  data,
  onRemove,
  onResend,
  loading,
}: InviteesListProps) => {
  const { t } = useTranslation();
  const count = data.length;

  const loggedInCount = useMemo(
    () =>
      data.filter(
        invitee => invitee.userLoginStatus === UserLoginStatus.Completed
      ).length,
    [data]
  );

  const isThereAnyInvitee = count > 0;
  const hasLoggedInUsers = loggedInCount > 0;

  return (
    <div className="border-border rounded-sm border-1 shadow-[0px_4px_6px_0px_rgba(0,0,0,0.09)]">
      <div
        className={cn('border-border border-b-1 px-3.5 pt-3.5 pb-1.5', {
          'border-none': !isThereAnyInvitee,
        })}
      >
        <div className="flex items-center justify-between">
          <p className="text-primary text-sm leading-[18px] font-semibold">
            {t('components.shareDialog.invited', { count })}
          </p>
          {isThereAnyInvitee && (
            <Tooltip
              content={
                hasLoggedInUsers
                  ? t('components.shareDialog.loggedInTooltip', {
                      count: loggedInCount,
                    })
                  : t('components.shareDialog.noLoggedInUsers')
              }
              TooltipContentProps={{
                variant: 'secondary',
                className: 'text-xs max-w-[320px] p-2.5',
              }}
            >
              <div className="flex items-center gap-1.5">
                <CheckCircle className="text-secondary size-5" />
                <p className="text-primary text-sm leading-[18px] font-semibold">
                  {t('components.shareDialog.loggedInCount', {
                    count: loggedInCount,
                  })}
                </p>
              </div>
            </Tooltip>
          )}
        </div>
      </div>
      {loading ? (
        <InviteesListSkeleton />
      ) : isThereAnyInvitee ? (
        <ul className="max-h-[250px] overflow-y-auto">
          {data.map((item, index) => (
            <InviteesListItem
              {...(onRemove && {
                onRemove: () => onRemove(item),
              })}
              {...(onResend && {
                onResend: () => onResend(item),
              })}
              data={item}
              key={`invitees-list-item-${index}`}
            />
          ))}
        </ul>
      ) : (
        <InformationWrapper
          icon={<EmptyInvitations className="text-primary size-[108px]" />}
          title={t('components.shareDialog.noData')}
          customTitleClassName="text-md font-medium leading-[18px]"
          className="mt-2 mb-6"
        />
      )}
    </div>
  );
};
